import React, { ReactNode, useCallback } from 'react'
import Fuse from 'fuse.js'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { Section, SectionList } from '../../../common/SectionList'
import {
  getListSectionSortedAndGroupedUsers,
  getUserFullName,
} from '../../../../helpers/user'
import { ResidentListItem } from '../../ResidentList/ResidentListItem'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { IUserModel } from '../../../../interfaces/models/users.interfaces'
import { unitQueries } from '../../../../api/units'
import { userQueries } from '../../../../api/users'

export type MemberListItem = { userId: string }

interface ResidentListBaseProps {
  membersList: MemberListItem[]
  setMembersListValue: (val: MemberListItem[]) => void
}

interface SectionResidentListItemProps extends ResidentListBaseProps {
  user: IUserModel
  society?: ISocietiesModel
  selected: boolean
}

export const SectionResidentListItem = ({
  user,
  society,
  membersList,
  setMembersListValue,
  selected,
}: SectionResidentListItemProps): JSX.Element => {
  return (
    <ResidentListItem
      key={user._id}
      user={user}
      society={society}
      selectMode
      selected={selected}
      onSelectedChange={(val) => {
        if (val) {
          setMembersListValue([...membersList, { userId: user._id }])
        } else {
          setMembersListValue(
            membersList.filter((_member) => _member.userId !== user._id)
          )
        }
      }}
    />
  )
}

interface ResidentListSectionProps extends ResidentListBaseProps {
  searchString: string
}

export const ResidentListSection = ({
  membersList,
  setMembersListValue,
  searchString,
}: ResidentListSectionProps): ReactNode => {
  const selectedMemberIdsMap = membersList?.reduce(
    (res: { [key: string]: boolean }, curr): { [key: string]: boolean } => {
      if (!(curr.userId in res)) {
        res[curr.userId] = true
      }
      return res
    },
    {}
  )

  const { society } = useCurrentSociety()

  const { data: societyUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society?._id)
  )

  const residentIds = new Set(
    societyUnits?.flatMap(
      (unit) => unit?.residentsList?.map((resident) => resident.userId) || []
    )
  )
  const { data: societyUsers } = useQueries({
    queries:
      [...residentIds].map((userId) => userQueries.getUser(userId)) || [],
    combine: (queries) => {
      return {
        data: queries.flatMap((query) => query.data || []),
        isLoading: queries.some((query) => query.isLoading),
      }
    },
  })

  const societyUsersWithUnits = societyUsers.map((user) => {
    return {
      user: {
        ...user,
        fullName: getUserFullName(user),
      },
      units:
        societyUnits?.filter((unit) =>
          unit.residentsList?.find((resident) => resident.userId === user._id)
        ) || [],
    }
  })

  const getFilteredUsers = useCallback(() => {
    const options = {
      threshold: 0.2,
      keys: [
        'user.fullName',
        'user.name',
        'user.surname',
        'units.title',
        'units.titleLegal',
      ],
    }

    const fuse = new Fuse(societyUsersWithUnits, options)

    const filteredUsers =
      searchString === ''
        ? societyUsersWithUnits
        : fuse.search(searchString).map((result) => result.item)

    return filteredUsers.map((_user) => _user.user)
  }, [societyUsersWithUnits, searchString])

  const users = getFilteredUsers()
  const usersList = getListSectionSortedAndGroupedUsers(users)

  const getSections = (): Section[] => {
    return usersList.map((section) => ({
      title: section.title,
      item: (
        <div className="space-y-4">
          {section.data.map((_user) => {
            return (
              <SectionResidentListItem
                key={_user._id}
                user={_user}
                society={society}
                selected={
                  selectedMemberIdsMap && _user._id in selectedMemberIdsMap
                }
                setMembersListValue={setMembersListValue}
                membersList={membersList}
              />
            )
          })}
        </div>
      ),
    }))
  }

  return <SectionList sections={getSections()} />
}
